<template>
  <div class="dashboard-panel">
    <div v-if="showCourierIntegrations">
      <portal target-el="#filter">
        <PrimaryFilter :menuOptions="menuList" :selectedCategory="selectedCategory" :enableSearchPopup="true"
          :searchParameters="courierSearchConstants" :preSearchedParams="searchedParams" :searchText="$t('MDM_SEARCH')"
          @searchByParameter="searchByParameter" />
      </portal>
      <head-panel :isLisLoadingCompleted="isListLoadingCompleted" :isCourierEvent="courierEvent" :filterEnabled="true"
        :depotListData="getDepotList" :transporterList="getTransporterList" @filter-change="filterChange" />
      <EntityList :data="getCategorizedList" :isListLoadingCompleted="isListLoadingCompleted" @load-more="loadData"
        @select-item="selectItem" />
      <EntityDetails :columnName="courierColumn" :selectedEntity="selectedEntity" />
    </div>
    <div v-else class="loading-app page-not-found">
      <h2>Page not found</h2>
      <p>
        Go back to <router-link :to="{ path: '/indents/outbound' }">Indents</router-link>
      </p>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import PrimaryFilter from '@/components/common/PrimaryFilter';
import { menuList, courierSearchConstants } from '@/constants/integrations';
import EntityList from './entityList';
import EntityDetails from './entityDetails';
import HeadPanel from '@common/components/HeadPanel';
import { mapGetters } from 'vuex';
import { interfaceEventType } from 'pando-constants';


export default {
  components: {
    HeadPanel,
    EntityList,
    EntityDetails,
    PrimaryFilter,
  },

  data() {
    return {
      searchedParams: {},
      courierList: [],
      category: null,
      entityDetails: null,
      courierSearchConstants,
      menuList,
      integrationType: 2,
      isListLoadingCompleted: true,
      courierEvent: true,
      courierDates: '',
      courierColumn: '',
      emptyData: false,
      selectedEntity: {},
      depotListData: {},
      selectedDepotId: [],
      selectedTransporterId: [],
      transporterList: { list: [] },
      transporters: [],
      interfaceEventType,
    };
  },
  computed: {
    ...mapGetters({
      depots: 'app/depots',
    }),
    showCourierIntegrations() {
      return this.$hasFeature('view_courier_integrations');
    },
    getCategorizedList() {
      return this.courierList.map(courier => ({
        ...courier,
        updatedAt: moment(courier.updated_at).format('MMM Do YYYY  - hh:mm A'),
      }));
    },
    selectedCategory() {
      const path = this.$router.history.current.name;
      return this.menuList.find(menu => menu.id === path);
    },
    getTransporterList() {
      return this.transporterList || {};
    },
    getDepotList() {
      return this.depotListData || {};
    },
  },
  async created() {
    await this.getTransporters();
    this.constructFilterList();
    this.loadSearchFromSession();
  },
  methods: {
    async filterChange({ type, category, dates, columnMap, depotIds, transporterId }) {
      this.integrationType = type;
      this.category = category;
      this.courierDates = dates;
      this.courierColumn = columnMap;
      this.defaultLoaded();
      this.entityDetails = null;
      this.selectedDepotId = depotIds;
      if (transporterId.includes(0)) {
        this.selectedTransporterId = [];
      } else {
        this.selectedTransporterId = transporterId.filter(ev => ev !== 0);
      }
      await this.loadData();
    },
    constructFilterList() {
      this.depotListData = {
        list: this.depots,
        selected: [
          {
            id: 0,
            name: 'All Depot',
          },
          ...this.depots,
        ],
      };
      this.transporterList = {
        list: this.transporters,
        selected: [
          {
            id: 0,
            name: 'All Transporters',
          },
          ...this.transporters,
        ],
      };
      this.selectedTransporterId = this.transporters.map(transporter => transporter.id);
      this.selectedDepotId = this.depots.map(depot => depot.id);
    },
    async getTransporters() {
      this.isListLoadingCompleted = false;
      const params = {};
      let dateObj = null;
      if (this.searchedParams && Object.keys(this.searchedParams).length) {
        Object.assign(params, { ...this.searchedParams });
      }
      if (this.selectedDepotId.length) {
        Object.assign(params, { depot_id: this.selectedDepotId });
      }
      if (
        this.courierDates &&
        this.courierDates.length &&
        this.courierDates.length === 2
      ) {
        dateObj = {
          start_date: this.courierDates[0],
          end_date: this.courierDates[1],
        };
      }
      this.transporters = await this.$store.dispatch('order/getCourierTransporters', {
        page: this.page,
        type: this.interfaceEventType.COURIER,
        category: this.category,
        dateObj,
        params,
      });
      this.isListLoadingCompleted = true;
    },
    loadSearchFromSession() {
      const sessionSearchParamsJSON = localStorage.getItem(`${this.$route.name}-search-popup`) || '{}';
      const sessionSearchParams = JSON.parse(sessionSearchParamsJSON);
      this.searchedParams = Object.assign({}, this.searchedParams, sessionSearchParams);
    },
    saveSearchToSession() {
      const sessionSearch = JSON.stringify(this.searchedParams);
      localStorage.setItem(`${this.$route.name}-search-popup`, sessionSearch);
    },
    async searchByParameter(params) {
      this.searchedParams = params;
      this.saveSearchToSession();
      this.defaultLoaded();
      await this.loadData();
    },

    defaultLoaded() {
      this.page = 1;
      this.emptyData = false;
      this.courierList = [];
      this.selectedEntity = {};
    },
    async loadData() {
      this.isListLoadingCompleted = false;
      if (!this.emptyData) {
        const params = {};
        let dateObj = null;
        if (this.searchedParams && Object.keys(this.searchedParams).length) {
          Object.assign(params, { ...this.searchedParams });
        }
        if (this.selectedDepotId.length) {
          Object.assign(params, { depot_id: this.selectedDepotId });
        }
        if (this.selectedTransporterId.length) {
          Object.assign(params, { transporter_id: this.selectedTransporterId });
        }
        if (
          this.courierDates &&
          this.courierDates.length &&
          this.courierDates.length === 2
        ) {
          dateObj = {
            start_date: this.courierDates[0],
            end_date: this.courierDates[1],
          };
        }
        let result = [];
        result = await this.$store.dispatch('order/getIntegrationData', {
          page: this.page,
          type: this.interfaceEventType.COURIER,
          category: this.category,
          dateObj,
          params,
        });
        if (result.length !== 0) {
          this.courierList = [...this.courierList, ...result];
          this.page += 1;
          this.emptyData = false;
        } else {
          this.emptyData = true;
          this.entityDetails = null;
        }
      }
      this.isListLoadingCompleted = true;
    },
    async selectItem(entity) {
      this.selectedEntity = entity;
    },
  },
};
</script>
